import React from 'react';
import { useTranslation } from 'react-i18next';
import { localizePath } from '../../seo/site';
import { motion } from 'framer-motion';

const WhyUs = () => {
  const { t, i18n } = useTranslation();

  const advantages = [
    {
      icon: '🚚',
      title: t('whyUs.advantages.reliability.title'),
      description: t('whyUs.advantages.reliability.description')
    },
    {
      icon: '❄️',
      title: t('whyUs.advantages.temperature.title'),
      description: t('whyUs.advantages.temperature.description')
    },
    {
      icon: '🌍',
      title: t('whyUs.advantages.coverage.title'),
      description: t('whyUs.advantages.coverage.description')
    },

    {
      icon: '⚡',
      title: t('whyUs.advantages.speed.title'),
      description: t('whyUs.advantages.speed.description')
    },
    {
      icon: '🛡️',
      title: t('whyUs.advantages.security.title'),
      description: t('whyUs.advantages.security.description')
    }
  ];

  const trustBadges = [
    {
      title: t('whyUs.trust.iso'),
      description: t('whyUs.trust.isoDesc')
    },
    {
      title: t('whyUs.trust.experience'),
      description: t('whyUs.trust.experienceDesc')
    },
    {
      title: t('whyUs.trust.fleet'),
      description: t('whyUs.trust.fleetDesc')
    },
    {
      title: t('whyUs.trust.routes'),
      description: t('whyUs.trust.routesDesc')
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('whyUs.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              {t('whyUs.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('whyUs.advantagesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('whyUs.advantagesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-6xl mb-4">{advantage.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{advantage.title}</h3>
                <p className="text-gray-600">{advantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('whyUs.trustTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('whyUs.trustSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustBadges.map((badge, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6 text-center"
              >
                <h3 className="text-2xl font-bold text-orange-500 mb-2">{badge.title}</h3>
                <p className="text-gray-600">{badge.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="section-padding bg-orange-500 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('whyUs.ctaTitle')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('whyUs.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={localizePath('/#request-form', i18n.language)}
                className="btn-secondary bg-white text-orange-500 hover:bg-gray-100 px-8 py-4 text-lg transition-colors duration-300"
              >
                {t('common.getQuote')}
              </a>
              <a
                href="tel:+48577083279"
                className="btn-secondary bg-transparent border-2 border-white text-white hover:bg-white hover:text-orange-500 px-8 py-4 text-lg transition-colors duration-300"
              >
                {t('common.callNow')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs; 